import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClassroomDto, YoutubeSearchResultDto, YoutubeVideoDto } from '../api/types.js';
import { ApiError } from '../api/client.js';
import { useApi } from '../api/ApiProvider.js';
import { isoDate } from '../lib/localDate.js';
import { ResetSettingsButton } from './ResetSettingsButton.js';
import formStyles from './settingsForm.module.css';
import styles from './YouTubePage.module.css';

export function YouTubePageSettings({ classroom }: { classroom: ClassroomDto }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const classroomId = classroom.id;
  const [pasteUrl, setPasteUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<YoutubeSearchResultDto[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);

  const videosQuery = useQuery({
    queryKey: ['youtube-videos', classroomId],
    queryFn: () => api.youtube.listVideos(classroomId),
  });

  const setVideos = (videos: YoutubeVideoDto[]) => {
    queryClient.setQueryData(['youtube-videos', classroomId], videos);
  };

  const add = useMutation({
    mutationFn: (body: Parameters<typeof api.youtube.addVideo>[1]) =>
      api.youtube.addVideo(classroomId, body),
    onSuccess: () => {
      setPasteUrl('');
      void queryClient.invalidateQueries({ queryKey: ['youtube-videos', classroomId] });
    },
  });

  const remove = useMutation({
    mutationFn: (videoId: string) => api.youtube.removeVideo(classroomId, videoId),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['youtube-videos', classroomId] }),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.youtube.reorderVideos(classroomId, { ids }),
    onSuccess: setVideos,
  });

  const resetAll = useMutation({
    mutationFn: async () => {
      const current = await api.youtube.listVideos(classroomId);
      await Promise.all(
        current.map((video) => api.youtube.removeVideo(classroomId, video.videoId)),
      );
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['youtube-videos', classroomId] }),
  });

  const search = useMutation({
    mutationFn: (q: string) => api.youtube.search(classroomId, q, isoDate()),
    onSuccess: (results) => {
      setSearchError(null);
      setSearchResults(results);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'youtube_search_unavailable') {
        setSearchError('YouTube search is not configured yet. Paste a YouTube link instead.');
      } else {
        setSearchError('YouTube search failed. Paste a YouTube link instead.');
      }
      setSearchResults([]);
    },
  });

  const videos = videosQuery.data ?? [];
  const addedVideoIds = useMemo(() => new Set(videos.map((video) => video.videoId)), [videos]);
  const visibleSearchResults = searchResults.filter((result) => !addedVideoIds.has(result.videoId));
  const busy = add.isPending || remove.isPending || reorder.isPending || resetAll.isPending;

  function submitPaste(e: FormEvent) {
    e.preventDefault();
    if (!pasteUrl.trim()) return;
    add.mutate({ videoUrl: pasteUrl.trim() });
  }

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    search.mutate(q);
  }

  function clearSearch() {
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    search.reset();
  }

  function addResult(result: YoutubeSearchResultDto) {
    add.mutate(
      {
        videoId: result.videoId,
        title: result.title,
        channelTitle: result.channelTitle,
        thumbnailUrl: result.thumbnailUrl,
        videoUrl: `https://www.youtube.com/watch?v=${result.videoId}`,
      },
      {
        onSuccess: () => {
          setSearchResults((current) => current.filter((item) => item.videoId !== result.videoId));
        },
      },
    );
  }

  function move(video: YoutubeVideoDto, direction: -1 | 1) {
    const index = videos.findIndex((v) => v.id === video.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= videos.length) return;
    const next = [...videos];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setVideos(next);
    reorder.mutate(next.map((v) => v.id));
  }

  return (
    <div className={`glass glass--solid ${formStyles.panel}`}>
      <header className={formStyles.header}>
        <div className={formStyles.headerText}>
          <h2 className={formStyles.heading}>YouTube playlist</h2>
          <p className={formStyles.description}>
            Add videos for a lesson. In morning meeting, played videos move out of the way until
            Reset playlist is tapped.
          </p>
        </div>
        <ResetSettingsButton
          pageLabel="YouTube"
          onReset={() => resetAll.mutate()}
          disabled={resetAll.isPending || videosQuery.isLoading}
        />
      </header>

      <div className={styles.layout}>
        <section className={styles.addColumn}>
          <form className={formStyles.form} onSubmit={submitPaste}>
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor="youtube-paste-url">
                Paste a YouTube link
              </label>
              <div className={styles.inlineForm}>
                <input
                  id="youtube-paste-url"
                  className={formStyles.input}
                  value={pasteUrl}
                  onChange={(e) => setPasteUrl(e.target.value)}
                  placeholder="https://youtu.be/..."
                  disabled={add.isPending}
                  data-testid="youtube-paste-input"
                />
                <button
                  type="submit"
                  className={styles.primaryButton}
                  disabled={add.isPending || !pasteUrl.trim()}
                  data-testid="youtube-paste-add"
                >
                  Add
                </button>
              </div>
              {add.isError ? (
                <p className={styles.error}>That does not look like a YouTube video link.</p>
              ) : null}
            </div>
          </form>

          <form className={formStyles.form} onSubmit={submitSearch}>
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor="youtube-search-query">
                Search YouTube
              </label>
              <div className={styles.inlineForm}>
                <div className={styles.searchInputWrap}>
                  <input
                    id="youtube-search-query"
                    className={formStyles.input}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="counting by tens song"
                    disabled={search.isPending}
                    data-testid="youtube-search-input"
                  />
                  {searchQuery || searchResults.length > 0 || searchError ? (
                    <button
                      type="button"
                      className={styles.clearSearchButton}
                      onClick={clearSearch}
                      aria-label="Clear YouTube search"
                      data-testid="youtube-search-clear"
                    >
                      X
                    </button>
                  ) : null}
                </div>
                <button
                  type="submit"
                  className={styles.primaryButton}
                  disabled={search.isPending || !searchQuery.trim()}
                  data-testid="youtube-search-submit"
                >
                  Search
                </button>
              </div>
              {searchError ? <p className={styles.error}>{searchError}</p> : null}
            </div>
          </form>

          {visibleSearchResults.length > 0 ? (
            <div className={styles.searchResults} data-testid="youtube-search-results">
              {visibleSearchResults.map((result) => (
                <button
                  key={result.videoId}
                  type="button"
                  className={styles.result}
                  onClick={() => addResult(result)}
                  disabled={add.isPending}
                  data-testid={`youtube-result-${result.videoId}`}
                >
                  <Thumb src={result.thumbnailUrl} title={result.title} />
                  <span className={styles.resultText}>
                    <strong>{result.title}</strong>
                    <span>{result.channelTitle}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </section>

        <section className={styles.playlistColumn} aria-label="Current playlist">
          <h3 className={formStyles.sectionTitle}>Current playlist</h3>
          {videosQuery.isLoading ? <p className={formStyles.hint}>Loading playlist...</p> : null}
          {!videosQuery.isLoading && videos.length === 0 ? (
            <p className={styles.empty}>No videos yet. Add a link or search YouTube.</p>
          ) : null}
          <div className={styles.videoList}>
            {videos.map((video, index) => (
              <article key={video.id} className={styles.videoRow} data-testid="youtube-video-row">
                <Thumb src={thumbFor(video)} title={video.title} />
                <div className={styles.videoText}>
                  <strong>{video.title}</strong>
                  <span>{video.channelTitle || 'YouTube'}</span>
                </div>
                <div className={styles.rowActions}>
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => move(video, -1)}
                    disabled={busy || index === 0}
                    aria-label={`Move ${video.title} earlier`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => move(video, 1)}
                    disabled={busy || index === videos.length - 1}
                    aria-label={`Move ${video.title} later`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => remove.mutate(video.videoId)}
                    disabled={busy}
                  >
                    Remove
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function Thumb({ src, title }: { src: string; title: string }) {
  return src ? (
    <img className={styles.thumb} src={src} alt="" loading="lazy" />
  ) : (
    <span className={styles.thumbFallback} aria-label={title}>
      ▶
    </span>
  );
}

function thumbFor(video: YoutubeVideoDto): string {
  return video.thumbnailUrl || `https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`;
}
